import React, { useEffect, useRef } from "react";
import { omit, isNil, isEmpty } from "lodash";
import {
  appIdSelector,
  accessIdSelectedSelector,
  isTourEnabledSelector,
} from "modules/Login/selectors/user";
import {Description as DescriptionIcon, Info as InfoIcon } from "@material-ui/icons";
import { makeStyles, IconButton, Typography, Grid, Tooltip } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { generateIncomeTax } from "./actions";
import { isLoadingDeclarationSelector } from "./selectors/declaration";
import { getStudents } from "./services/incometax";
import {
  isLoadingStudentsSelector,
  studentsDataSelector,
  hasLoadedStudentsSelector,
} from "./selectors/students";
import Table from "components/Table";
import { FormattedMessage, injectIntl } from "react-intl";

const useStyles = makeStyles(() => ({
  notFoundStudents: {
    textAlign: "center",
  },
}));

const columns = (onGenerate, messages, isTotal = false) => [
  {
    title: messages["income-tax--table-name"],
    field: "studentName",
  },
  {
    title: messages["income-tax-table-student-code"],
    field: "studentCode",
  },
  {
    title: messages["income-tax-table-unity"],
    render: row => row.unity?.name || "",
  },
  {
    title: messages["income-tax-table-base-year"],
    render: row => row.year?.split("-")[0] || "",
  },
  {
    title: isTotal
      ? messages["income-tax-table-receipt-total"]
      : messages["income-tax-table-tax"],
    field: "declaration",
    render: (row) => (isTotal || row.incomeTax) ? (
      <IconButton
        id={isTotal ? "icon-button-total" : "icon-button"}
        onClick={() => onGenerate(row, isTotal, isTotal ? "icon-button-total" : "icon-button")}
      >
        <DescriptionIcon />
      </IconButton>
    ) : (
      <Tooltip
        title={messages["income-tax-table-receipt-not-enabled-for-current-year"]}
      >
        <IconButton
          id={isTotal ? "icon-button-total" : "icon-button"}
        >
          <InfoIcon />
        </IconButton>
      </Tooltip>
    )
  },
];

export default injectIntl(({ intl: { messages } }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const tableRef = useRef();
  const isLoadingDeclaration = useSelector(isLoadingDeclarationSelector);
  const hasLoadedStudents = useSelector(hasLoadedStudentsSelector);
  const isLoadingStudents = useSelector(isLoadingStudentsSelector);
  const appId = useSelector(appIdSelector);
  const data = useSelector(studentsDataSelector);
  const clientCode = useSelector(accessIdSelectedSelector);
  const isTourOpen = useSelector(isTourEnabledSelector);

  const onGenerate = (item, isTotal, id) => {

    const data = {
      ...omit(item, ["studentName", "tableData"]),
      reportType: isTotal ? 1 : 0
    }

    dispatch(
      generateIncomeTax(
        appId,
        isTotal,
        data,
        id
      )
    );
  };

  useEffect(() => {
    if (isTourOpen && !isEmpty(data)) {
      return;
    }

    dispatch(getStudents(appId, isNil(clientCode) ? '' : clientCode));

  }, [dispatch, appId, isTourOpen]);

  if (isNil(data) || data.length === 0) {
    return (
      <Typography
        gutterBottom
        component="p"
        color="textPrimary"
        className={classes.notFoundStudents}
      >
        <FormattedMessage id="income-tax-students-not-found" />
      </Typography>
    );
  }

  const receiptData = data && data.length && data
    .filter(x => x.studentCode)
    .reduce((acc, curr) => {
      const existing = acc.find(item => item.studentCode === curr.studentCode);
      if (!existing || parseInt(curr.year.split("-")[0], 10) > parseInt(existing.year.split("-")[0], 10)) {
        return [...acc.filter(item => item.studentCode !== curr.studentCode), curr];
      }
      return acc;
    }, []);
  return (
    <Grid
      container
      spacing={2}
    >
      <Grid
        item
        xs={12}
      >
        <Table
          isLoading={isLoadingStudents || isLoadingDeclaration}
          tableRef={tableRef}
          columns={columns(onGenerate, messages, false)}
          options={{
            sorting: false,
            selection: false,
            padding: "dense",
            headerStyle: {
              paddingRight: 5,
              padding: "16px 0px",
            },
          }}
          data={hasLoadedStudents ? data : []}
          title={messages["income-tax--title"] || "Declarações de Imposto de Renda"}
        />
      </Grid>
      <Grid
        item
        xs={12}
      >

        <Table
          isLoading={isLoadingStudents || isLoadingDeclaration}
          tableRef={tableRef}
          columns={columns(onGenerate, messages, true)}
          options={{
            sorting: false,
            selection: false,
            search: false,
            padding: "dense",
            headerStyle: {
              paddingRight: 5,
              padding: "16px 0px",
            },
          }}
          data={hasLoadedStudents ? receiptData : []}
          title={messages["income-tax--total-title"] || "Rendimentos Total (Ano Base e Anteriores)"}
        />
      </Grid>
    </Grid>
  );
});
